import { useCallback } from 'react'
import { isAxiosError } from 'axios'
import { Loader2, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { LexProduct } from '@/shared/hooks/api/product'
import { useDeleteProduct } from '@/shared/hooks/api/product'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'

type ProductDeleteDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  product: LexProduct
  linkedChatCount: number
  onDeleted?: () => void
}

export function ProductDeleteDialog({
  open,
  onOpenChange,
  product,
  onDeleted,
  linkedChatCount,
}: ProductDeleteDialogProps) {
  const { t } = useTranslation(['productCatalog', 'common'])
  const { mutateAsync: deleteProduct, isPending: isDeleting } =
    useDeleteProduct()

  const productName =
    product.name?.trim() || t('productCatalog:fallbackProductTitle')

  const handleDelete = useCallback(async () => {
    try {
      await deleteProduct(product.id)
      toast.success(t('productCatalog:delete.success'))
      onOpenChange(false)
      onDeleted?.()
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        toast.error(t('productCatalog:delete.notFound'))
        onOpenChange(false)
        onDeleted?.()
        return
      }
      toast.error(t('productCatalog:delete.error'))
    }
  }, [deleteProduct, onDeleted, onOpenChange, product.id, t])

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('productCatalog:delete.title')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className='space-y-2'>
              <p>{t('productCatalog:delete.description', { productName })}</p>
              {linkedChatCount > 0 ? (
                <p>
                  {t('productCatalog:delete.linkedChatsWarning', {
                    count: linkedChatCount,
                  })}
                </p>
              ) : null}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <Button
            type='button'
            variant='destructive'
            onClick={() => {
              void handleDelete()
            }}
            disabled={isDeleting}
            className='gap-2'
          >
            {isDeleting ? (
              <>
                <Loader2 className='size-4 animate-spin' aria-hidden />
                {t('productCatalog:delete.loading')}
              </>
            ) : (
              <>
                <Trash2 className='size-4' aria-hidden />
                {t('productCatalog:delete.confirm')}
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
